import { DateTime } from "luxon";

export interface TimeWindow {
  start: DateTime;
  end: DateTime;
}

export interface BisectedFetchResult<T> {
  items: T[];
  // Ranges that never returned, already merged so adjacent leaves read as one
  // gap. Empty on a healthy fetch.
  failedWindows: TimeWindow[];
  // Nothing at all could be read. That is an outage rather than one unservable
  // record, and callers should say so instead of showing an empty schedule.
  failedEntirely: boolean;
  // The distinct reasons the failed windows failed, so a gap can be attributed
  // to whatever refused the range instead of only being counted.
  failureReasons: string[];
}

export interface FailureKind {
  // Whether a narrower range could still succeed. One unservable record can be
  // cornered by halving, but an expired token or an iPad with no network fails
  // the same way at every size, and halving only multiplies the damage.
  rangeDependent: boolean;
  // What went wrong, in a form that belongs in telemetry rather than in front
  // of an operator.
  reason: string;
}

// Assume the range is worth halving, since a caller that cannot tell the
// difference is no worse off than before it could.
const UNCLASSIFIED: FailureKind = { rangeDependent: true, reason: "unknown" };

// As narrow as the bisect goes: each extra level costs another failed request
// to localise a smaller slice of the day.
const MIN_WINDOW_MINUTES = 30;

// A ceiling on waiting, not on requests: the API client already spends ~2s
// retrying each 5xx. Deliberately a clock rather than a request count, so
// several bad records in one day are not mistaken for an outage.
const MAX_DURATION_MS = 20_000;

/**
 * Fetches a time range, and on failure retries it as halves until the failure is
 * cornered in a leaf window.
 *
 * One unservable record poisons every request whose range covers it, so a
 * narrower range is enough to get the rest of the day back. A healthy range
 * still costs exactly one request, and a failure that no range would survive
 * costs one too.
 */
export async function fetchInBisectedWindows<T>({
  window,
  fetchWindow,
  knownFailedWindows = [],
  classifyFailure = () => UNCLASSIFIED,
  minWindowMinutes = MIN_WINDOW_MINUTES,
  maxDurationMs = MAX_DURATION_MS,
}: {
  window: TimeWindow;
  fetchWindow: (window: TimeWindow) => Promise<T[]>;
  // Where the holes were last time. Cornering a failure costs one failed
  // request per level, so a day already known to be poisoned skips straight to
  // the shape it settled on before instead of halving its way back down.
  knownFailedWindows?: TimeWindow[];
  // What a failure means, which only the caller's API knows. Left out, every
  // failure is halved.
  classifyFailure?: (error: unknown) => FailureKind;
  minWindowMinutes?: number;
  maxDurationMs?: number;
}): Promise<BisectedFetchResult<T>> {
  const items: T[] = [];
  const failedWindows: TimeWindow[] = [];
  const failureReasons = new Set<string>();
  const deadline = Date.now() + maxDurationMs;

  // Returns whether the range yielded nothing at all, so a parent can tell one
  // unreadable half from two. Sequential on purpose: bisecting in parallel
  // would multiply load on an endpoint already returning errors.
  async function fetchRange(range: TimeWindow): Promise<boolean> {
    if (Date.now() >= deadline) {
      failedWindows.push(range);
      failureReasons.add("deadline");
      return true;
    }

    try {
      items.push(...(await fetchWindow(range)));
      return false;
    } catch (error) {
      const { rangeDependent, reason } = classifyFailure(error);
      failureReasons.add(reason);

      const tooNarrowToSplit =
        range.end.diff(range.start, "minutes").minutes <= minWindowMinutes;

      // Halving an auth failure or a dead network just fails six more times
      // and leaves the caller knowing exactly what it knew after the first.
      if (!rangeDependent || tooNarrowToSplit) {
        failedWindows.push(range);
        return true;
      }
    }

    const midpoint = range.start.plus({
      milliseconds: range.end.diff(range.start).toMillis() / 2,
    });
    const startFailed = await fetchRange({ start: range.start, end: midpoint });
    const endFailed = await fetchRange({ start: midpoint, end: range.end });

    return startFailed && endFailed;
  }

  // Each leg is fetched whole and only bisected if it fails, so a healed hole
  // is not bisected and a new hole elsewhere is still cornered.
  const plan = planFrom(window, knownFailedWindows);
  const outcomes: boolean[] = [];
  for (const leg of plan) {
    outcomes.push(await fetchRange(leg));
  }
  const failedEntirely = outcomes.every((failed) => failed);

  return {
    items,
    failedWindows: mergeAdjacent(failedWindows),
    failedEntirely,
    failureReasons: [...failureReasons],
  };
}

// A run of consecutive bad records bisects into several touching leaves. Merged,
// they seed the next fetch as one leg and read as one gap in telemetry.
function mergeAdjacent(windows: TimeWindow[]): TimeWindow[] {
  const sorted = [...windows].sort(
    (a, b) => a.start.toMillis() - b.start.toMillis()
  );

  const merged: TimeWindow[] = [];
  for (const window of sorted) {
    const previous = merged.at(-1);
    if (previous == null || previous.end < window.start) {
      merged.push(window);
      continue;
    }
    merged[merged.length - 1] = {
      start: previous.start,
      end: previous.end > window.end ? previous.end : window.end,
    };
  }

  return merged;
}

// The known holes, plus the stretches between them, in order. Without any known
// holes that is just the whole window, which is the single request a healthy
// day has always cost.
function planFrom(window: TimeWindow, known: TimeWindow[]): TimeWindow[] {
  const holes = mergeAdjacent(
    known.filter((hole) => hole.start >= window.start && hole.end <= window.end)
  );
  if (holes.length === 0) {
    return [window];
  }

  const plan: TimeWindow[] = [];
  let cursor = window.start;
  for (const hole of holes) {
    if (cursor < hole.start) {
      plan.push({ start: cursor, end: hole.start });
    }
    plan.push(hole);
    cursor = hole.end;
  }
  if (cursor < window.end) {
    plan.push({ start: cursor, end: window.end });
  }

  return plan;
}
